/**
 * Moroccan darija overlays for the structured content modules (services, trades, cities, guides).
 *
 * An overlay mirrors the shape of the source object; every leaf is a `DarijaText` holding the Latin script
 * (`darija`) and Arabic script (`darija_ar`) versions of the corresponding fr/en/ar text. `applyDarija` adds
 * those two keys next to the existing ones: fr / en / ar values are never touched.
 */
export interface DarijaText {
  darija: string;
  darija_ar: string;
}

export const isDarijaText = (value: unknown): value is DarijaText =>
  !!value && typeof value === 'object' && typeof (value as DarijaText).darija === 'string' && typeof (value as DarijaText).darija_ar === 'string';

/* eslint-disable @typescript-eslint/no-explicit-any */
export const applyDarija = <T>(target: T, overlay: unknown): T => {
  if (isDarijaText(overlay)) {
    const base = typeof target === 'string' ? { fr: target } : (target as any);
    return { ...base, darija: overlay.darija, darija_ar: overlay.darija_ar };
  }
  if (Array.isArray(overlay)) {
    return (target as any).map((item: unknown, index: number) => (overlay[index] === undefined ? item : applyDarija(item, overlay[index]))) as T;
  }
  if (overlay && typeof overlay === 'object') {
    const out: any = { ...(target as any) };
    for (const key of Object.keys(overlay)) {
      out[key] = applyDarija((target as any)[key], (overlay as any)[key]);
    }
    return out;
  }
  return target;
};

/** Applies `overlays[slug]` to `base[slug]` for every slug of the overlay map; other entries are returned untouched. */
export const applyDarijaMap = <T extends Record<string, unknown>>(base: T, overlays: Record<string, unknown>): T => {
  const out: Record<string, unknown> = { ...base };
  for (const slug of Object.keys(overlays)) {
    if (slug in out) out[slug] = applyDarija(out[slug], overlays[slug]);
  }
  return out as T;
};
